// PostPreview.jsx
import React from "react";
import { Box, Typography, Button, makeStyles } from "@material-ui/core";
import Markdown from "markdown-to-jsx";

const useStyles = makeStyles(() => ({
  frame: {
    border: "1px solid #e0e0e0",
    borderRadius: 12,
    padding: 16,
    background: "#fff",
  },
  group: {
    border: "1px dashed #e0e0e0",
    borderRadius: 10,
    padding: 12,
    marginBottom: 16,
    background: "#fafafa",
  },
  block: {
    borderRadius: 8,
    padding: 8,
    background: "#fff",
  },
  gap: {
    display: "flex",
    gap: 12,
    flexDirection: "column",
  },
  videoWrapper: {
    position: "relative",
    paddingTop: "56.25%", // 16:9
    borderRadius: 8,
    overflow: "hidden",
    background: "#000",
  },
  iframe: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    border: 0,
  },
}));

const textVariantByStyle = (style) => {
  switch (style) {
    case "title":
      return "h5";
    case "subtitle":
      return "subtitle1";
    default:
      return "body1";
  }
};

const buttonVariantByType = (t) => {
  switch (t) {
    case "L":
      return "text";
    case "S":
      return "outlined";
    default:
      return "contained";
  }
};

// Estrae l'ID da ID puro o URL YouTube comuni
function extractYouTubeId(input = "") {
  const str = String(input).trim();
  if (!str) return "";
  // già un ID?
  if (/^[a-zA-Z0-9_-]{11}$/.test(str)) return str;
  try {
    const u = new URL(str);
    if (u.hostname === "youtu.be") return u.pathname.slice(1);
    const v = u.searchParams.get("v");
    if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;
    const m = u.pathname.match(/\/(embed|v)\/([a-zA-Z0-9_-]{11})/);
    if (m) return m[2];
  } catch {
    // non è una URL, prova a catturare un ID in mezzo alla stringa
    const m = str.match(/([a-zA-Z0-9_-]{11})/);
    if (m) return m[1];
  }
  return "";
}

// <li> custom per markdown con displayName (evita warning eslint)
function MdLi({ children }) {
  return (
    <li>
      <Typography variant="body1" component="span">
        {children}
      </Typography>
    </li>
  );
}
MdLi.displayName = "MdLi";

// Override per mantenere stile Material nel markdown
const mdOverrides = {
  h1: { component: Typography, props: { variant: "h4", gutterBottom: true } },
  h2: { component: Typography, props: { variant: "h5", gutterBottom: true } },
  h3: { component: Typography, props: { variant: "h6", gutterBottom: true } },
  h4: {
    component: Typography,
    props: { variant: "subtitle1", gutterBottom: true },
  },
  h5: {
    component: Typography,
    props: { variant: "subtitle2", gutterBottom: true },
  },
  h6: {
    component: Typography,
    props: { variant: "body1", gutterBottom: true },
  },
  p: { component: Typography, props: { variant: "body1", paragraph: true } },
  li: { component: MdLi },
};

export default function PostPreview({ post, noFrame = false }) {
  const classes = useStyles();
  const groups = post?.groups ?? [];

  return (
    <Box className={noFrame ? undefined : classes.frame}>
      {!noFrame && (
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Anteprima
        </Typography>
      )}

      {!groups.length && (
        <Typography variant="body2" color="textSecondary">
          Nessun contenuto da mostrare.
        </Typography>
      )}

      {groups.map((g, gi) => (
        <Box
          key={gi}
          className={classes.group}
          style={{
            background: g?.backgroundColor || undefined,
          }}
        >
          <Box
            className={classes.gap}
            style={{
              flexDirection: g?.direction === "R" ? "row" : "column",
            }}
          >
            {(g?.blocks ?? []).map((b, bi) => (
              <Box key={bi} className={classes.block}>
                {/* TESTO */}
                {b?.type === "T" && (
                  <Typography variant={textVariantByStyle(b?.content?.style)}>
                    {b?.content?.content ?? ""}
                  </Typography>
                )}

                {/* IMMAGINE */}
                {b?.type === "I" && b?.content?.src && (
                  <img
                    src={b.content.src}
                    alt=""
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: 6,
                      display: "block",
                      margin: "0 auto", // centrata orizzontalmente
                    }}
                  />
                )}

                {/* BOTTONE */}
                {b?.type === "B" && (
                  <Button
                    variant={buttonVariantByType(b?.content?.type)}
                    color="primary"
                  >
                    {b?.content?.label || "Bottone"}
                  </Button>
                )}

                {/* LISTA */}
                {b?.type === "L" && Array.isArray(b?.content?.texts) && (
                  <>
                    {b?.content?.type === "O" ? (
                      <ol style={{ margin: 0, paddingLeft: 18 }}>
                        {b.content.texts.map((t, i) => (
                          <li key={i}>
                            <Typography
                              variant={textVariantByStyle(b?.content?.style)}
                            >
                              {t}
                            </Typography>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {b.content.texts.map((t, i) => (
                          <li key={i}>
                            <Typography
                              variant={textVariantByStyle(b?.content?.style)}
                            >
                              {t}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}

                {/* MARKDOWN */}
                {b?.type === "MD" && (
                  <Markdown options={{ overrides: mdOverrides }}>
                    {b?.content?.markdown ?? ""}
                  </Markdown>
                )}

                {/* YOUTUBE */}
                {b?.type === "YT" && b?.content?.videoId && (
                  <div className={classes.videoWrapper}>
                    <iframe
                      className={classes.iframe}
                      src={`https://www.youtube.com/embed/${extractYouTubeId(
                        b.content.videoId
                      )}`}
                      title="YouTube video player"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
